import { useState } from "react"
import { BriefcaseIcon } from "@heroicons/react/24/outline"
import { Experience } from "./types"
import { Section, Item, EmptyState } from "./ui"
import { Modal } from "../../components/modal/Modal"

export const ExperienceBlock = ({
  items,
  onAdd,
  onDelete,
}: {
  items: Experience[]
  onAdd: (data: Omit<Experience, "id">) => void
  onDelete: (id: string) => void
}) => {
  const [open, setOpen] = useState(false)

  const [company, setCompany] = useState("")
  const [position, setPosition] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [description, setDescription] = useState("")
  const [current, setCurrent] = useState(false)

  const resetForm = () => {
    setCompany("")
    setPosition("")
    setStartDate("")
    setEndDate("")
    setDescription("")
    setCurrent(false)
  }

  const handleSave = () => {
    if (!company.trim() || !position.trim() || !startDate) return

    onAdd({
      company,
      position,
      start_date: startDate,
      ...(current ? {} : endDate ? { end_date: endDate } : {}),
      description,
    })

    resetForm()
    setOpen(false)
  }

  return (
    <>
      <Section
        id="experience"
        title="Experience"
        icon={BriefcaseIcon}
        iconColor="text-blue-500 bg-blue-50"
        description="Your professional background and work history"
        onAdd={() => setOpen(true)}
      >
        {!items.length && (
          <EmptyState
            icon={BriefcaseIcon}
            title="No experience added yet"
            subtitle="Add your first role to strengthen your profile"
            ctaLabel="Add experience"
            onAdd={() => setOpen(true)}
          />
        )}

        {items.map((e) => (
          <Item
            key={e.id}
            title={`${e.position} · ${e.company}`}
            subtitle={formatPeriod(e.start_date, e.end_date)}
            description={e.description}
            onDelete={() => onDelete(e.id)}
          />
        ))}
      </Section>

      {/* MODAL */}
      <Modal
        open={open}
        onClose={() => {
          resetForm()
          setOpen(false)
        }}
        title="Add experience"
      >
        <div className="space-y-3">
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Company"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <input
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="Position"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          <div className="flex gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {!current && (
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={current}
              onChange={(e) => {
                setCurrent(e.target.checked)
                if (e.target.checked) setEndDate("")
              }}
              className="rounded border-gray-300 text-blue-600"
            />
            I currently work here
          </label>

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What did you do, technologies, achievements…"
            rows={3}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={() => { resetForm(); setOpen(false) }}
              className="border border-gray-200 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 hover:bg-blue-700 transition"
              disabled={!company || !position || !startDate}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

/* ===================== HELPERS ===================== */

const formatPeriod = (start: string, end?: string) => {
  const format = (d: string) =>
    new Date(d).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })

  return `${format(start)} – ${end ? format(end) : "Present"}`
}
